// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useLocation } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/layout/language-switcher";
import { NotificationPanel } from "@/components/layout/notification-panel";
import { usePageTitle } from "@/components/layout/page-title-context";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { UserDropdown } from "@/components/layout/user-dropdown";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebugMode } from "@/lib/debug-mode";
import { NAV_ITEMS } from "@/lib/navigation";

function openCommandPalette() {
  document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }));
}

export function Header() {
  const { t } = useTranslation("back-office");
  const pageTitle = usePageTitle();
  const { pathname } = useLocation();
  const { debugMode } = useDebugMode();

  const PageIcon = useMemo(() => {
    if (pageTitle?.icon) return pageTitle.icon;
    const basePath = pageTitle?.breadcrumbs?.[0]?.to ?? pathname;
    return NAV_ITEMS.find((item) => basePath.startsWith(item.to))?.icon;
  }, [pageTitle?.icon, pageTitle?.breadcrumbs, pathname]);

  return (
    <header className="sticky top-0 z-50 flex h-14 shrink-0 items-center gap-2 border-b bg-background">
      <div className="flex w-full items-center gap-2 px-4">
        {!pageTitle && (
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded-sm" />
            <Skeleton className="h-4 w-32" />
          </div>
        )}
        {pageTitle && (
          <Breadcrumb>
            <BreadcrumbList>
              {PageIcon && <PageIcon className="h-4 w-4 text-muted-foreground" />}
              {pageTitle.breadcrumbs?.map((crumb, i) => (
                <Fragment key={i}>
                  {i > 0 && <BreadcrumbSeparator />}
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild className="text-base leading-none pt-px">
                      <Link to={crumb.to}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </Fragment>
              ))}
              {pageTitle.breadcrumbs && pageTitle.breadcrumbs.length > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                <BreadcrumbPage className="text-base font-medium leading-none pt-px">{pageTitle.title}</BreadcrumbPage>
              </BreadcrumbItem>
              {pageTitle.description && <span className="text-sm text-muted-foreground">{pageTitle.description}</span>}
            </BreadcrumbList>
          </Breadcrumb>
        )}

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          className="hidden h-8 gap-2 text-muted-foreground md:flex"
          onClick={openCommandPalette}
        >
          <Search className="h-3.5 w-3.5" />
          <span className="text-xs">{t("header.commands")}</span>
          <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">&#8984;</span>K
          </kbd>
        </Button>

        <div className="w-1" />
        <LanguageSwitcher />
        <ThemeToggle />
        {debugMode && <NotificationPanel />}
        <div className="w-1" />
        <UserDropdown />
      </div>
    </header>
  );
}
